import type { ReactNode } from "react";

import { PageHeader } from "../page-header";

export interface SettingsPageShellProps {
  title: string;
  description?: ReactNode;
  /** Optional right-aligned actions in the page header. */
  actions?: ReactNode;
  /** Opt in only when this page is rendered outside a shell-owned main landmark. */
  asMain?: boolean;
  children: ReactNode;
}

/**
 * Standard wrapper for every page rendered inside the /settings
 * section. Gives every page a consistent header and content width.
 * The persistent product shell owns the main landmark, so this wrapper renders
 * a div by default. Standalone consumers may opt in with `asMain`.
 *
 * The title row composes the canonical {@link PageHeader} block (rail
 * rule #7 — page title rows are owned by PageHeader, never hand-rolled).
 * `description` maps to PageHeader's `description`, `actions` to its
 * `toolbar`. Settings pages render no PageNav (the top-nav breadcrumb
 * already says "Settings"), so this title IS the page H1 — it renders in the
 * unified serif page-title treatment (PageHeader's default), same as every mode
 * section. Content is `mx-auto`-centered: `max-w-3xl`
 * caps the reading width well below the shell's 1200px content well, so
 * the column would otherwise hug the left edge — unbalanced now that the
 * settings nav floats in the RIGHT gutter (the ContextRail), not a left
 * rail. Centering matches the docs reader (centred body + right-gutter
 * rail) and the wider integrations pages (`mx-auto max-w-5xl`).
 */
export function SettingsPageShell({
  title,
  description,
  actions,
  asMain = false,
  children,
}: SettingsPageShellProps) {
  const Root = asMain ? "main" : "div";
  return (
    <Root className="mx-auto max-w-3xl px-6 py-6">
      <PageHeader
        title={title}
        description={description}
        toolbar={actions}
        className="mb-6"
      />
      <div className="space-y-6">{children}</div>
    </Root>
  );
}
